import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, isEmpty } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import Form from 'reactstrap/lib/Form';

import { logger } from 'client/utils/logger/logger';

import { IDM_METHODS } from 'client/data/models/profile/profile-constants';
import { createNicePhone } from 'site-modules/shared/utils/phone-format';
import { validation } from 'site-modules/shared/components/validation/validation';
import { validationHelper } from 'site-modules/shared/components/validation/validation-helper/validation-helper';

import { LoadingSpinner } from 'site-modules/profile/components/shared/loading-spinner/loading-spinner';
import { settingsWrapperComponent } from 'site-modules/profile/components/settings/settings-wrapper/settings-wrapper';

import { filterFormValues, formatToIdm, getFormValues } from './edit-personal-info-helper';
import { FormInput } from './form-input';

import './edit-personal-info.scss';

const GENDER_SELECT_OPTIONS = [
  { value: 'Unknown', label: 'Select' },
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

const PHONE_SELECT_OPTIONS = [
  { value: '', label: 'Select' },
  { value: 'Mobile', label: 'Mobile' },
  { value: 'LandLine', label: 'Home' },
  { value: 'Other', label: 'Other' },
];

export const VALIDATORS = {
  firstName: {
    test: validationHelper.validateName,
  },
  lastName: {
    test: validationHelper.validateName,
  },
  phone: {
    test: (params, options) => validationHelper.validatePhoneNumber({ ...params, id: 'insider' }, options),
    minLength: 10,
    isAsync: true,
  },
};

function EditPersonalInfoUI({
  saveChanges,
  isSubmitDisabled = false,
  validationClasses = {},
  showActiveSpinner,
  isDeletedUser = false,
  profileData,
  isEditMode = false,
  toggleMode,
  validated = [],
  validateField,
  validateForm,
  resetValidation,
  errorMap = new Map(),
}) {
  const initialFormValues = useRef(getFormValues(profileData));
  // eslint-disable-next-line react-hooks/refs -- initialize the controlled form once from profile data
  const [formValues, setFormValues] = useState(initialFormValues.current);

  function onChange({ target: { name, value } }) {
    setFormValues({ ...formValues, [name]: value });
  }

  function onBlur({ target: { name, value } }) {
    if (errorMap.has(name) || (validated.includes(name) && initialFormValues.current[name] !== formValues[name])) {
      validateField(name, value);
    }
  }

  function onSubmit(e) {
    e.preventDefault();
    return validateForm(initialFormValues.current, formValues)
      .then(() => !errorMap.size)
      .then(isValid => {
        if (isValid) {
          const filteredValues = filterFormValues(initialFormValues.current, formValues);
          const formattedData = formatToIdm(filteredValues);
          if (!isEmpty(formattedData)) {
            return saveChanges(formattedData, IDM_METHODS.MIXED_UPDATE);
          }
        }
        return Promise.resolve();
      })
      .then(() => {
        resetValidation();
        toggleMode();
      })
      .catch(error => {
        logger.error(error.message);
      });
  }

  useEffect(() => {
    initialFormValues.current = getFormValues(profileData);
    setFormValues({ ...initialFormValues.current });
  }, [profileData]);

  useEffect(() => {
    setFormValues(initialFormValues.current);
    resetValidation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  const email = get(profileData, 'identifiers.email[0]');

  const commonProps = {
    onChange,
    onBlur,
    labelClassName: 'm-0',
    inputClassName: 'size-16',
  };

  const errorKeys = Array.from(errorMap.keys());

  const editModeRender = (
    <Fragment>
      <FormInput
        {...commonProps}
        id="edit-info-fname"
        label="First name"
        name="firstName"
        value={formValues.firstName}
        validationClass={validationClasses.firstName}
        trackingId="provide_first_name"
      />
      <FormInput
        {...commonProps}
        id="edit-info-lname"
        label="Last name"
        name="lastName"
        value={formValues.lastName}
        validationClass={validationClasses.lastName}
        trackingId="provide_last_name"
      />
      <FormInput
        {...commonProps}
        type="select"
        id="select-gender-option"
        label="Gender"
        name="gender"
        value={formValues.gender}
        options={GENDER_SELECT_OPTIONS}
      />
      <div className="d-flex">
        <FormInput
          {...commonProps}
          id="edit-info-phone"
          label="Phone"
          name="phone"
          type="tel"
          pattern="000-000-0000"
          placeholder="___-___-____"
          value={formValues.phone}
          validationClass={validationClasses.phone}
        />
        {/* eslint-disable react-hooks/refs */}
        <FormInput
          {...commonProps}
          type="select"
          id="select-phone-option"
          name="phoneType"
          label="Type"
          value={formValues.phoneType}
          options={PHONE_SELECT_OPTIONS}
          className="second"
          labelClassName="m-0 ms-1 ms-md-2"
          inputClassName={classnames(commonProps.inputClassName, 'ms-1')}
        />
        {/* eslint-enable react-hooks/refs */}
      </div>
      <FormInput
        {...commonProps}
        id="edit-info-zip-code"
        label="Zip Code"
        name="zipCode"
        value={formValues.zipCode}
        validationClass={validationClasses.zipCode}
      />
    </Fragment>
  );

  const displayModeRender = (
    <Fragment>
      <div className="mb-1">
        {!!(formValues.firstName || formValues.lastName) && (
          <Fragment>
            {formValues.firstName} {formValues.lastName}{' '}
            {!!formValues.gender && formValues.gender !== 'Unknown' && `(${formValues.gender})`}
          </Fragment>
        )}
        <div>{email}</div>
      </div>
      {!!formValues.phone && <div className="mb-1">{createNicePhone(formValues.phone)}</div>}
      {!!formValues.zipCode && <div className="mb-1">{formValues.zipCode}</div>}
    </Fragment>
  );

  return (
    <Form className={classnames({ 'form-disabled': showActiveSpinner })} onSubmit={onSubmit}>
      {isEditMode ? editModeRender : displayModeRender}
      {isEditMode && errorKeys && (
        <div className="form-group h7">
          <ul className="edit-info-form-error ps-1 py-0_75">
            {errorKeys.map(key => (
              <li className="small text-danger" key={key}>
                {errorMap.get(key)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {isEditMode && (
        <div className="text-end">
          <Button size="sm" color="link" className="text-capitalize size-16 me-0_5" onClick={toggleMode}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            color="outline-primary-b"
            className="size-16"
            data-tracking-id="change_personal_info"
            disabled={isSubmitDisabled || showActiveSpinner || isDeletedUser}
          >
            Save
            {showActiveSpinner && <LoadingSpinner wrapperClassNames="d-inline-block ps-0_25" />}
          </Button>
        </div>
      )}
    </Form>
  );
}

EditPersonalInfoUI.propTypes = {
  profileData: PropTypes.shape({}).isRequired,
  saveChanges: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  validateForm: PropTypes.func.isRequired,
  resetValidation: PropTypes.func.isRequired,
  errorMap: PropTypes.shape({}),
  validationClasses: PropTypes.shape({}),
  isSubmitDisabled: PropTypes.bool,
  validated: PropTypes.arrayOf(PropTypes.string),
  showActiveSpinner: PropTypes.bool.isRequired,
  className: PropTypes.string,
  isDeletedUser: PropTypes.bool,
  isEditMode: PropTypes.bool,
  toggleMode: PropTypes.func.isRequired,
};

export const EditPersonalInfo = validation(settingsWrapperComponent(EditPersonalInfoUI), VALIDATORS);
